import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

/** Single asset within a STAC item (e.g. one GeoTIFF) */
export interface StacAsset {
  href?: string;
  title?: string;
  type?: string;
  description?: string;
  bbox?: number[];
  properties?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface StacItem {
  type?: string;
  id?: string;
  properties?: Record<string, unknown>;
  assets?: Record<string, StacAsset>;
  geometry?: unknown;
  bbox?: number[];
  [key: string]: unknown;
}

export interface StacItemCollection {
  type?: string;
  features?: StacItem[];
  items?: StacItem[]; /* some servers use items instead of features */
  numberMatched?: number;
  numberReturned?: number;
}

/**
 * Fetches STAC API - Collections Items.
 * URL: {base}/stac/collections/{collectionId}/items
 * Forest Stack: https://geoserver.forest-stack.digivan.forest.rajasthan.gov.in/stac/collections/{id}/items
 */
@Injectable({ providedIn: 'root' })
export class OgcStacApiService {
  private http = inject(HttpClient);

  /**
   * Fetch STAC items for a collection.
   * @param collectionId - Collection/dataset ID (e.g. UUID)
   * @param accessToken - Optional Bearer token for protected endpoints
   */
  fetchCollectionItems(
    collectionId: string,
    accessToken?: string
  ): Promise<StacItemCollection> {
    const base = (
      (environment as { ogcApiPath?: string; api_ogc_server_domain?: string }).ogcApiPath ??
      (environment as { api_ogc_server_domain?: string }).api_ogc_server_domain ??
      ''
    ).replace(/\/$/, '');
    const url = `${base}/stac/collections/${encodeURIComponent(collectionId)}/items`;

    let headers = new HttpHeaders({
      Accept: 'application/json, text/plain, */*',
    });
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const obs = this.http.get<StacItemCollection>(url, { headers });
    return firstValueFrom(obs);
  }

  /**
   * Download STAC asset as blob (e.g. TIFF raster) from items endpoint.
   * @param collectionId - Collection ID
   * @param itemId - STAC item/asset ID
   * @param accessToken - Optional Bearer token
   */
  async downloadStacAsset(
    collectionId: string,
    itemId: string,
    accessToken?: string
  ): Promise<Blob> {
    const base = (
      (environment as { ogcApiPath?: string; api_ogc_server_domain?: string }).ogcApiPath ??
      (environment as { api_ogc_server_domain?: string }).api_ogc_server_domain ??
      ''
    ).replace(/\/$/, '');
    const url = `${base}/stac/collections/${encodeURIComponent(collectionId)}/items/${encodeURIComponent(itemId)}`;

    let headers = new HttpHeaders({ Accept: 'application/octet-stream, image/tiff, */*' });
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    const obs = this.http.get(url, { responseType: 'blob', headers });
    return firstValueFrom(obs);
  }
}
